import { useState, useEffect, useRef } from "react";
import { ComboBox, Item, TagGroup, View, Flex, Divider, TextField, Heading, Checkbox, DisclosurePanel, Disclosure, Accordion, DisclosureTitle, Link, ActionButton } from "@adobe/react-spectrum";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import { ListDistributionGroupRequest } from "../../../../../services/soap/main/requests/ListDistributionGroupRequest";
import { ContactFormData } from "../AddNewCompany";
import { CloseIcon, EditIcon } from "../../../../../components/protected/Icons/IconsLib";
import { usePreloadAssets } from "../../../../../hooks/UsePreloadAssets";
import { Check, ExclamationTriangle, Plus } from "react-bootstrap-icons";
import { useViewInfo } from "../../../../../hooks/UseViewInfo";
import { useTranslation } from "react-i18next";

interface IIndustrySectionProps {
  data: ContactFormData["Industries"];
  onChange: (data: ContactFormData["Industries"]) => void;
  expandedSectionKey:any
  setExpandedSectionKey:(data:any)=>void;
  validationErrors:any
  setValidationErrors : (data:any)=>void;
  fieldConfig:any
  validationStatus:any
  setValidationStatus:(data:any)=>void;
}

const IndustrySection: React.FC<IIndustrySectionProps> = ({ data, onChange, expandedSectionKey,
  setExpandedSectionKey,
  validationErrors,
  setValidationErrors,
  fieldConfig,
  validationStatus,
  setValidationStatus }) => {
  const { store, mainService } = useDependency();
  const {industries} = usePreloadAssets();
  const [selectedIndustries, setSelectedIndustries] = useState<any[]>(data);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());
  const [highlightedGroupId, setHighlightedGroupId] = useState<string | null>(null);
  const firstInputRef = useRef<any>(null);
  const [isSectionRequired,setIsSectionRequired] = useState(false);
  const{isMobile} = useViewInfo();
  const nameFieldRefs = useRef<Record<number, any | null>>({});
  const { t } = useTranslation('contacts');
  const _t = (key: string) => t(`addNewCompany.${key}`, { ns: 'contacts' });
  const _tt = (key: string) => t(`contact_common.${key}`, { ns: 'contacts' });

      useEffect(() => {
          if(fieldConfig?.industry?.required
          ){
            setValidationStatus((prev:any) => ({ ...prev, ['industriesSection']: false }));
            setIsSectionRequired(true)
          } 
        }, [fieldConfig]);

  const handleGroupSelection = (industryId: any, index?: number) => {
  const selectedIndustry = industries.find((g) => g.id === industryId);

  if (!selectedIndustry) {
    setValidationErrors({ general: "Industry not found" });
    return;
  }

  const newIndustry = {
    industry: selectedIndustry.id,
    industryName: selectedIndustry.name,
    description: index !== undefined ? data[index]?.description || "" : "",
  };

  let newIndustries;

  if (typeof index === "number") {
    newIndustries = [...data];
    newIndustries[index] = newIndustry;
  } else {
    const existingIndex = data.findIndex((g) => g.industry === industryId);

    if (existingIndex !== -1) {
      setHighlightedGroupId(industryId);
      setTimeout(() => setHighlightedGroupId(null), 1000);
      return;
    }
    newIndustries = [...data, newIndustry];
  }

  setValidationErrors({});
  onChange(newIndustries);
};


  const onAdd = ()=>{
        const newIndustry = { industry: '', industryName: '', description: "" };
        const newIndustries = [...data, newIndustry];
        onChange(newIndustries);
        const index = data.length;
      setTimeout(() => {
            nameFieldRefs.current[index]?.focus();
      }, 200); 

  }

  const handleRemoveGroup = (index: number) => {
    const newGroups = data.filter((_, i) => i !== index);
    //setSelectedIndustries(newGroups);
    onChange(newGroups);
  };

  const onEdit = (index: number) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) newSet.delete(index);
      else newSet.add(index);
      return newSet;
    });
  };

  const updatePrimary = (value: boolean, index: number) => {
    const primaryCount = data.filter(item => item.isPrimary).length;
    if (!value && primaryCount === 1) {
      return;
    }
    const newData = data.map((item, i) => ({
      ...item,
      isPrimary: i === index ? value === true ? '1' : '0' : '0',
    }));
    onChange(newData);
  };

  const handleSectionExpandedChange = () => {
    setExpandedSectionKey((prevKey:any) => prevKey.has("industriesSection") ? new Set() : new Set(["industriesSection"]));
  };

  const handleConfirm = ()=>{
    const errors:any = {};
    if(fieldConfig?.industry?.required && data.length === 0){
      errors.industry = 'Industry is required';
    }
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setValidationStatus((prev:any) => ({ ...prev, ['industriesSection']: false }));
      return;
    }
    setValidationStatus((prev:any) => ({ ...prev, ['industriesSection']: true }));
    setExpandedSectionKey(new Set(['mailSubscriptionSection']))
  }

  const validateRequiredFields = ()=>{
    const errors:any = {};
    if(fieldConfig?.industry?.required && data.length === 0){
      errors.industry = 'Industry is required';
    }
    if (Object.keys(errors).length > 0) {
      setValidationStatus((prev:any) => ({ ...prev, ['industriesSection']: false }));
      return;
    }
    setValidationStatus((prev:any) => ({ ...prev, ['industriesSection']: true }));
  }

  const buttonRef = useRef<any>(null);
          useEffect(() => {
          if (expandedSectionKey?.has?.("industriesSection")) {
            setTimeout(() => {
              buttonRef.current?.focus();
            }, 200); 
          }
        }, [expandedSectionKey]);

  // useEffect(()=>{
  //   if (firstInputRef.current) {
  //       firstInputRef.current.focus();
  //     }
  // },[expandedSectionKey])

  return (
      <Accordion isQuiet expandedKeys={expandedSectionKey} onExpandedChange={handleSectionExpandedChange}>
            <Disclosure id="industriesSection">
              <DisclosureTitle>
                <Flex justifyContent="space-between" alignItems="center" width="100%">
                  <View><span>{_t(`industries`)}</span><span style={{color:'#B0BEC5',fontFamily:'Adobe Clean'}}>{isSectionRequired && ` (${_tt('required')})`}</span></View>
                   {validationStatus.industriesSection ? <Check size={20} color="#1976D2" /> :<ExclamationTriangle size={20} color="#FF9800" />}
                </Flex>
              </DisclosureTitle>
              <DisclosurePanel>
              <Divider size='M' UNSAFE_style={{marginBottom:10}}/>
              <Flex direction="column" gap="size-100" width="100%">
        {data.map((tag, index) => (
          <View
            key={index}
            UNSAFE_style={{
              transition: "color 0.5s ease-in-out",
              color: highlightedGroupId === tag.industry ? "red" : "black",
            }}
          >
            <Flex key={index} gap="size-100" direction="row" width="100%">
              {/* <TagGroup
                items={data}
                onRemove={() => handleRemoveGroup(index)}
                aria-label="Removable TagGroup example"
                width="30%"
              >
                <Item aria-label={tag.industryName}>{tag.industryName}</Item>
              </TagGroup> */}
              <ComboBox
          ref={(el:any) => nameFieldRefs.current[index] = el}
          label={_t("industries")}
          aria-label={_t("industries")}
          onSelectionChange={(selectedKey) => handleGroupSelection(selectedKey,index)}
          onBlur = {()=>validateRequiredFields()}
          width='50%'
          isRequired={fieldConfig?.industry?.required}
          selectedKey={tag.industry}
          errorMessage={validationErrors.industry}
          validationState={validationErrors.industry ? 'invalid' : undefined}
        >
          {industries.map((industry) => (
            <Item aria-label={industry.name} key={industry.id}>{industry.name}</Item>
          ))}
        </ComboBox>
              <TextField
                    label={_tt('note')}
                    aria-label="Notes"
                    value={tag.description}
                    onChange={(description) => {
                      const newData = [...data];
                      newData[index] = { ...tag, description };
                      onChange(newData);
                    }}
                    width="50%"
                  />
              {/* <a onClick={() => onEdit(index)}>
                <EditIcon size={20} />
              </a> */}
              <Checkbox UNSAFE_style={{marginTop:25}} isSelected={data[index].isPrimary === '1'}
              onChange={(value)=>updatePrimary(value,index)}></Checkbox>
              <ActionButton 
                              UNSAFE_style={{marginTop:25}}
                              isQuiet = {isMobile ? false : true}
                              onPress={() => handleRemoveGroup(index)}
              
                            >
                              {isMobile ? _tt('button_remove') : <CloseIcon size={18} />}
                            </ActionButton>
            </Flex>
          </View>
        ))}
        {data.length > 0 && 
          <Divider size='M' aria-label="" />
        }
        <Flex justifyContent={'space-between'} marginTop={10} gap={'size-50'}>
                    <View>
               <ActionButton ref={buttonRef} onPress={onAdd} isQuiet UNSAFE_style={{color:'#1976D2'}}><Plus size={22} color='#1976D2' /> {_t('button_add_industry')}</ActionButton>
            </View>
            <View>
              <ActionButton onPress={handleConfirm} isQuiet UNSAFE_style={{color:'#1976D2'}}><Check size={22} color='#1976D2' /> {_tt('button_confirm')}</ActionButton> {/*active:#1976D2, disabled:#B0BEC5*/}
            </View>
                </Flex>
      </Flex>
      <Divider size='M' UNSAFE_style={{marginTop:10}}/>
              </DisclosurePanel>
              </Disclosure>
              </Accordion>
  );
};

export default IndustrySection;
